import type { ProfileFileResponse } from 'types'
import type { ClientType, ResourceType } from 'types'

import { useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { basePaths, paths } from 'paths'
import { useStore } from 'store'
import copy from 'copy.json'

import Checkbox, { CheckboxLabel } from 'components/Checkbox'
import Button from 'components/Button'
import Badge from 'components/Badge'
import Scroll from 'components/Scroll'
import Spinner from 'components/Spinner'

const text = copy.page.file_upload_confirmation
const button = copy.common.button

export const formatNormalizedProfileDate = (date: string) => date.replace(/^\d{4}-/, '')

type Props = {
  clientType: ClientType
  resourceType: ResourceType
}

export default function FileUploadConfirmation({ clientType, resourceType }: Props) {
  const { api } = useStore()
  const { buyerId, sellerId, demandId, offerId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [applyMultiplier, setApplyMultiplier] = useState(false)
  const { search, file_upload, success, format_choice } = paths
  const [loading, setLoading] = useState(false)

  let clientId: string | undefined
  let resourceId: string | undefined

  if (clientType === 'buyer') {
    clientId = buyerId
    resourceId = demandId
  }

  if (clientType === 'seller') {
    clientId = sellerId
    resourceId = offerId
  }

  const {
    errors,
    warnings,
    preview_data,
    total_power,
    data_start_date,
    data_end_date,
    profile_coverage
  } = location.state as ProfileFileResponse

  const handleConfirm = async () => {
    setLoading(true)
    await api.acceptProcessedResourceProfileFile(resourceType, Number(resourceId), applyMultiplier)

    setLoading(false)
    // TODO: handle error
    navigate(
      `${basePaths.admin_panel}/${clientType}/${search}/${clientId}/${file_upload}/${resourceId}/${success}`
    )
  }

  const handleReject = async () => {
    await api.rejectProcessedResourceProfileFile(resourceType, Number(resourceId))

    // TODO: handle error
    navigate(`${basePaths.admin_panel}/${clientType}/${search}/${clientId}`, { state: resourceId })
  }

  return (
    <Background>
      <Content>
        <h1>{errors?.length || warnings?.length ? text.heading_error : text.heading}</h1>

        {preview_data && (
          <Preview>
            <h2>{text.subheading}</h2>

            <div>
              <Scroll>
                <table>
                  <thead>
                    <tr>
                      <th>{text.table_heading}</th>
                      {Object.values(preview_data)[0].map((_, index) => (
                        <th key={index}>{index + 1}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {Object.entries(preview_data)
                      .slice(0, 10)
                      .map(([date, values], index) => (
                        <tr key={index}>
                          <td>{formatNormalizedProfileDate(date)}</td>
                          {values.map((value, index) => (
                            <td key={index}>{value}</td>
                          ))}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </Scroll>

              <div style={{ display: 'flex', gap: '72px' }}>
                <Detail>
                  <span>{text.total_power}</span>
                  <span>
                    {total_power} {copy.common.MWh}
                  </span>
                </Detail>

                <Detail>
                  <span>{text.time_range}</span>
                  <span>
                    {formatNormalizedProfileDate(data_start_date)} -{' '}
                    {formatNormalizedProfileDate(data_end_date)}
                  </span>
                </Detail>

                <Detail>
                  <span>{text.percent_coverage}</span>
                  <span>
                    <Badge offer>{`${profile_coverage}%`}</Badge>
                  </span>
                </Detail>
              </div>
            </div>

            <CheckboxLabel>
              <Checkbox
                checked={applyMultiplier}
                onChange={event => setApplyMultiplier(event.currentTarget.checked)}
              />
              {text.checkbox}
            </CheckboxLabel>
          </Preview>
        )}

        {errors && (
          <Errors>
            <h2>{text.subheading_error}</h2>

            <div>
              {errors.slice(0, 10).map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          </Errors>
        )}

        {warnings?.length > 0 && (
          <Errors>
            <h2>{text.subheading_warning}</h2>

            <div>
              {warnings.slice(0, 10).map((warning, index) => (
                <div key={index}>
                  <div>{warning}</div>
                </div>
              ))}
            </div>
          </Errors>
        )}

        <ButtonsWrapper>
          {errors && (
            <Button
              onClick={() =>
                navigate(
                  `${basePaths.admin_panel}/${clientType}/${search}/${clientId}/${file_upload}/${resourceId}/${format_choice}`
                )
              }
            >
              {button.undo}
            </Button>
          )}

          {!errors && preview_data && (
            <Button onClick={handleConfirm}>
              {loading ? <Spinner size={16} /> : button.confirm}
            </Button>
          )}

          <Button secondary onClick={handleReject}>
            {button.close}
          </Button>
        </ButtonsWrapper>
      </Content>
    </Background>
  )
}

const Background = styled.div`
  /* display: flex; */
  align-items: center;
  justify-content: center;

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  background: rgba(39, 67, 47, 0.89);
  overflow-y: auto;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;

  padding: 36px 109px;
  margin: 24px;

  background: var(--white);
  border-radius: 4px;

  h1 {
    font: var(--600-24);
    text-align: center;
  }

  h2 {
    font: var(--600-16);
  }
`

const Preview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  > div {
    padding: 16px;
    background: var(--very-light-blue);
  }

  table {
    width: 100%;
    border: 1px solid var(--light-gray);
    border-radius: 4px;
    border-collapse: collapse;
    text-align: left;

    thead {
      background: var(--very-light-blue);
    }

    tbody {
      background: var(--white);
    }

    th,
    td {
      min-width: 100px;
      border: 1px solid var(--light-gray);
      padding: 10px 8px;
    }
  }
`

const Errors = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  > div {
    padding: 16px;
    background: var(--very-light-blue);

    > div {
      padding: 8px 10px;
      border: 1px solid var(--light-gray);
      font: var(--400-12);
      color: var(--red);
    }
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  gap: 8px;
  width: 100%;
  max-width: 312px;
`

const Detail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font: var(--600-13);
  margin-top: 16px;

  span:nth-child(2) {
    font-weight: 400;
  }
`
